import { router } from "@inertiajs/react";

import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import { formatAmount, formatDate, formatTime } from "@/lib/formatting";
import { usePermissions } from "@/lib/permissions";
import {
  courses,
  exportExcelStatisticsCourses,
  statisticsShowCourses,
} from "@/routes";
import type { Course } from "@/types";

function exportUrl(path: string, filters: Record<string, string>): string {
  // Keep blank values: an explicit empty param (e.g. "status=") tells the
  // backend NOT to apply that filter's default.
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

type Props = {
  data: Course[];
  pagy: PagyMeta;
  filters: Record<string, string>;
  categories: string[];
  houses: string[];
  centers: { id: number; name: string }[];
  amounts: number[];
};

const USER_CATEGORY_LABELS: Record<string, string> = {
  senora: "Señora",
  universitaria: "Universitaria",
  numeraria_auxiliar: "Numeraria Auxiliar",
  agregada: "Agregada",
  numeraria: "Numeraria",
  la_cuesta: "S V",
  la_cuesta_sn: "S SV",
  la_cuesta_cp: "CP SV",
};

const USER_CATEGORY_OPTIONS = [
  { label: "Todas", value: "" },
  ...Object.entries(USER_CATEGORY_LABELS).map(([value, label]) => ({
    label,
    value,
  })),
];

const ORIGIN_LABELS: Record<string, string> = {
  los_tilos: "Mujer",
  la_cuesta: "Hombre",
};

const COLUMNS: Column<Course>[] = [
  {
    header: "Identificador",
    cell: (c) => (c.identifier as string | null) || "-",
  },
  {
    header: "Tipo de curso",
    cell: (c) => (c.category_name as string | null) || "-",
  },
  {
    header: "Fecha Inicio",
    cell: (c) => formatDate(c.start_date),
  },
  {
    header: "Hora",
    cell: (c) => formatTime(c.start_time),
  },
  {
    header: "Fecha Término",
    cell: (c) => formatDate(c.end_date),
  },
  {
    header: "Hora",
    cell: (c) => formatTime(c.end_time),
  },
  {
    header: "Casa",
    cell: (c) => (c.house_name as string | null) || "-",
  },
  {
    header: "Aporte",
    cell: (c) => formatAmount(c.amount),
  },
  {
    header: "Inscripciones",
    cell: (c) => (c.enrollments_summary as string | null) || "-",
  },
  {
    header: "Categoría",
    cell: (c) =>
      c.user_category
        ? (USER_CATEGORY_LABELS[c.user_category] ?? c.user_category)
        : "-",
  },
  {
    header: "Público/Privado",
    cell: (c) => (c.is_public ? "Público" : "Privado"),
  },
  {
    header: "Centro a Cargo",
    cell: (c) => (c.center_name as string | null) || "-",
  },
  {
    header: "Sexo",
    cell: (c) => ORIGIN_LABELS[c.origin] ?? "-",
  },
];

export default function CoursesStatistics({
  data: rows,
  pagy,
  filters,
  categories,
  houses,
  centers,
  amounts,
}: Props) {
  const permissions = usePermissions();

  const yearStartIso = `${new Date().getFullYear()}-01-01`;

  const actions: RowAction<Course>[] = [
    {
      label: "Ver",
      href: (c) => statisticsShowCourses(c.id).url,
      variant: "primary",
    },
  ];

  const filterFields: FilterField[] = [
    {
      key: "identifier",
      label: "Identificador",
      type: "text",
      placeholder: "Buscar por identificador...",
    },
    {
      key: "category",
      label: "Tipo de Curso",
      type: "select",
      options: [
        { label: "Todos", value: "" },
        ...categories.map((name) => ({ label: name, value: name })),
      ],
    },
    {
      label: "Fecha",
      type: "date-range",
      keyFrom: "date_from",
      keyTo: "date_to",
      defaultFrom: yearStartIso,
    },
    {
      key: "house",
      label: "Casa",
      type: "select",
      options: [
        { label: "Todas", value: "" },
        ...houses.map((name) => ({ label: name, value: name })),
      ],
    },
    {
      key: "amount",
      label: "Aporte",
      type: "select",
      options: [
        { label: "Todos", value: "" },
        ...amounts.map((amount) => ({
          label: formatAmount(amount),
          value: String(amount),
        })),
      ],
    },
    {
      key: "closed",
      label: "Estado",
      type: "select",
      default: "true",
      options: [
        { label: "Todos", value: "" },
        { label: "Cerrados", value: "true" },
        { label: "Abiertos", value: "false" },
      ],
    },
    ...(permissions.canViewAdminCourseFilters
      ? ([
          {
            key: "user_category",
            label: "Categorías de usuario",
            type: "select",
            options: USER_CATEGORY_OPTIONS,
          },
          {
            key: "course_center",
            label: "Centro a Cargo",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              ...centers.map((c) => ({
                label: c.name,
                value: String(c.id),
              })),
            ],
          },
          {
            key: "is_public",
            label: "Público/Privado",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              { label: "Público", value: "true" },
              { label: "Privado", value: "false" },
            ],
          },
          {
            key: "origin",
            label: "Sexo",
            type: "select",
            options: [
              { label: "Todos", value: "" },
              { label: "Mujer", value: "los_tilos" },
              { label: "Hombre", value: "la_cuesta" },
            ],
          },
        ] satisfies FilterField[])
      : []),
  ];

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(courses.statistics().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <div className="w-full py-10">
      <div className="px-6 sm:px-8 lg:px-12">
        <h1 className="mb-6 font-sans text-3xl font-semibold text-ink">
          Resumen general
        </h1>

        <FilterBar
          fields={filterFields}
          values={filters}
          onChange={(newFilters) =>
            navigate({ ...newFilters, page: 1 }, { replace: true })
          }
        />
      </div>

      <DataTable
        columns={COLUMNS}
        rows={rows}
        keyExtractor={(c) => c.id}
        actions={actions}
        utilities={
          permissions.canExportCourses ? (
            <a
              href={exportUrl(exportExcelStatisticsCourses().url, filters)}
              className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
            >
              Exportar Excel
            </a>
          ) : undefined
        }
        pagy={pagy}
        onPageChange={(page) => navigate({ ...filters, page })}
        emptyMessage="No hay cursos registrados."
      />
    </div>
  );
}
